import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import addDays from 'date-fns/add_days';
import format from 'date-fns/format';
import {
  divide as _divide,
  filter as _filter,
  get as _get,
  round as _round,
  uniq as _uniq
} from 'lodash';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { PolicySelectors } from '@amfam/policy/data-access';
import {
  CustomerEmailRequest,
  MessageModel,
  MessageRequest,
  NotificationRequestCustomerEmail,
  Policy,
  ReplaceVehicleRequestCustomerEmail,
  RiskModel
} from '@amfam/policy/models';
import { userQuery } from '@amfam/shared/user';
import { Agent, agentQuery, fromAgentActions, initialAgent } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { UtilService } from '@amfam/shared/utility/shared-services';

import * as fromRoot from '../../../core/store';
import * as replaceVehiclePolicyActions from '../../../core/store/replace-vehicle-policy/replace-vehicle-policy.actions';
import * as replaceVehicleActions from '../../../core/store/replace-vehicle/replace-vehicle.actions';
import { ReplaceVehicleReviewComponent } from '../../replace-vehicle/replace-vehicle-review/replace-vehicle-review.component';

@Injectable()
export class ReplaceVehicleEmailGuard {
  premiumDetails: any = {
    quoteExpirationDate: new Date(),
    current: {},
    new: {}
  };

  private vehicleId;
  private partnerId: string;
  private customerId: string;
  private customerEmail: string;
  private customerName: string;

  private selectedPolicy: Policy;
  private policyList: Policy[];
  private selectedProducer: Agent;

  public newVehicle: string;
  private matchingvehicle: RiskModel;

  constructor(
    private store: Store<fromRoot.RootState>,
    private utilService: UtilService
  ) {}

  canDeactivate(
    component: ReplaceVehicleReviewComponent,
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    nextState: RouterStateSnapshot
  ): any {
    if (nextState.url.toString().indexOf('/policies/replace-vehicle') === -1) {
      this.store.dispatch(new replaceVehiclePolicyActions.ReplaceVehiclePolicySelectClearAction());
      combineLatest(
        this.store.select(userQuery.selectUserState),
        this.store.select(BrandSelectors.selectPartnerId),
        this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
        this.store.select(PolicySelectors.selectAdvanceAutoPolicyRisks),
        this.store.select(fromRoot.getReplaceVehiclePremium),
        this.store.select(fromRoot.getReplaceVehicleNewVehicle),
        this.store.select(agentQuery.getAgentEntities),
        (user, partner, policies, risks, newPremium, newVehicle, agents) => {
          return {
            user: user,
            partner: partner,
            policies: policies,
            vehicles: risks,
            newPremium: newPremium,
            newVehicle: newVehicle,
            agents: agents
          };
        }
      )
        .pipe(take(1))
        .subscribe(fromState => {
          this.partnerId = fromState.partner;
          this.customerName = _get(fromState, 'user.firstName', '');
          this.customerId = _get(fromState, 'user.customerId', '');
          this.customerEmail = _get(fromState, 'user.emailAddress', '');

          this.vehicleId = _get(route, 'params.id');
          this.policyList = fromState.policies;

          let matchingPolicy;

          const matchingVehicle = fromState.vehicles.find(vehicle => {
            return _get(vehicle, 'id') === this.vehicleId;
          });

          if (matchingVehicle) {
            const policyNumber = matchingVehicle.policyNumber;

            this.matchingvehicle = matchingVehicle;

            matchingPolicy = fromState.policies.find(policy => {
              return _get(policy, 'policyNumber') === policyNumber;
            });

            if (matchingPolicy) {
              const currentTermMonths = Number(matchingPolicy.termDescription.split(' ')[0]);
              const monthlyCost = _round(
                _divide(matchingPolicy.totalPremium, currentTermMonths),
                2
              );

              this.premiumDetails.current.description = 'Current';
              this.premiumDetails.current.fullCost = Number(
                _get(matchingPolicy, 'totalPremium', 0)
              ).toFixed(2);
              this.premiumDetails.current.policyTermMonths = currentTermMonths;
              this.premiumDetails.current.monthlyCost = monthlyCost;

              this.premiumDetails.new.description = 'New';
              this.premiumDetails.new.fullCost = Number(
                _get(fromState, 'newPremium.totalPremium', 0)
              ).toFixed(2);
              this.premiumDetails.new.policyTermMonths = fromState.newPremium.termMonths;
              this.premiumDetails.new.monthlyCost = Number(
                _get(fromState, 'newPremium.monthlyPremium', 0)
              ).toFixed(2);
              this.premiumDetails.new.newVehicle = fromState.newVehicle;
              this.premiumDetails.new.newVehicleVin = fromState.newVehicle.vin;

              this.premiumDetails.orderNumber = fromState.newPremium.workOrderNumber;

              if (
                fromState.newVehicle &&
                fromState.newVehicle.year &&
                fromState.newVehicle.make &&
                fromState.newVehicle.model
              ) {
                this.newVehicle =
                  fromState.newVehicle.year +
                  ' ' +
                  fromState.newVehicle.make +
                  ' ' +
                  fromState.newVehicle.model;
              }

              this.selectedPolicy = matchingPolicy;

              // JP: Check if we've loaded producer already - dispatch load if not
              this.selectedProducer =
                fromState.agents[_get(this.selectedPolicy, 'assignedProducer.producerIdNum')];
              if (!this.selectedProducer) {
                this.store.dispatch(
                  new fromAgentActions.LoadAgentAction(
                    Object.assign({}, initialAgent, {
                      id: _get(this.selectedPolicy, 'assignedProducer.producerIdNum')
                    })
                  )
                );
                this.retrieveAgents();
              }
            }
          }
        });

      const customerNotificationRequest: NotificationRequestCustomerEmail = {
        email: new ReplaceVehicleRequestCustomerEmail(this.getCustomerEmailRequestModel()),
        message: new MessageModel(this.getMessageModel()),
        templateId: 'ReplaceVehicleNoQuoteSubmitted'
      };

      this.store.dispatch(
        new replaceVehicleActions.ReplaceVehicleNotifyCustomerAction(customerNotificationRequest)
      );
    }
    return true;
  }

  // JP: Since we must take(1) in the guard, making this a function called after we dispatch action
  private retrieveAgents(): void {
    // JP: After Load action has been dispatched, subscribe again and fetch the correct producer
    if (!this.selectedProducer) {
      combineLatest(
        this.store.select(agentQuery.getAreAllAgentsLoaded),
        this.store.select(agentQuery.getAgentEntities),
        (loaded, agents) => {
          return {
            loaded: loaded,
            agents: agents
          };
        }
      )
        .pipe(
          filter(data => data.loaded),
          take(1)
        )
        .subscribe(data => {
          this.selectedProducer =
            data.agents[_get(this.selectedPolicy, 'assignedProducer.producerIdNum')];
        });
    }
  }

  private getCustomerEmailRequestModel(): CustomerEmailRequest {
    const customerEmailRequest: CustomerEmailRequest = {
      customerEmailId: this.customerEmail,
      customerId: this.customerId,
      partnerId: this.partnerId
    };
    return customerEmailRequest;
  }

  private getMessageModel(): MessageRequest {
    let drivers: string;
    let coverages: string;
    let discounts: string;
    let fees: string;

    for (const vehicleNamedInsured of _uniq(
      _filter(this.matchingvehicle.vehicleRoles, { roleType: 'PrimaryDriver' })
    )) {
      if (drivers) {
        drivers += ', ' + _get(vehicleNamedInsured, 'name');
      } else {
        drivers = _get(vehicleNamedInsured, 'name');
      }
    }
    for (const vehicleCoverage of _uniq(_get(this.matchingvehicle, 'vehicleCoverages', []))) {
      if (coverages) {
        coverages += ', ' + _get(vehicleCoverage, 'name');
      } else {
        coverages = _get(vehicleCoverage, 'name');
      }
    }
    // Pull vehicle-level discounts
    for (const vehicleDiscount of _uniq(_get(this.matchingvehicle, 'vehicleDiscounts', []))) {
      if (discounts) {
        discounts += ', ' + _get(vehicleDiscount, 'name');
      } else {
        discounts = _get(vehicleDiscount, 'name');
      }
    }
    // Pull policy-level discounts
    for (const policyDiscount of _uniq(_get(this.matchingvehicle, 'policy.policyDiscounts', []))) {
      if (discounts) {
        discounts += ', ' + _get(policyDiscount, 'name');
      } else {
        discounts = _get(policyDiscount, 'name');
      }
    }
    // Pull vehicle-level fees
    for (const vehicleFee of _uniq(_get(this.matchingvehicle, 'vehicleFees', []))) {
      if (fees) {
        fees += ', ' + _get(vehicleFee, 'name');
      } else {
        fees = _get(vehicleFee, 'name');
      }
    }
    // Pull policy-level fees
    for (const policyFee of _uniq(_get(this.matchingvehicle, 'policy.policyFees', []))) {
      if (fees) {
        fees += ', ' + _get(policyFee, 'name');
      } else {
        fees = _get(policyFee, 'name');
      }
    }
    for (const vehicleSurchanges of _uniq(
      _get(this.matchingvehicle, 'policy.policySurcharges', [])
    )) {
      if (fees) {
        fees += ', ' + _get(vehicleSurchanges, 'name');
      } else {
        fees = _get(vehicleSurchanges, 'name');
      }
    }
    const quoteExpDate: any = _get(this.premiumDetails, 'quoteExpirationDate');
    const messageModelRequest: MessageRequest = {
      firstName: this.customerName,
      policyNumber: this.utilService.maskPolicyNumber(this.selectedPolicy.policyNumber),

      riskInformation: this.matchingvehicle.description,
      existingVehicleVin: this.matchingvehicle.vin,
      totalPremium: _get(this.premiumDetails, 'current.fullCost'),
      monthlyPremium: _get(this.premiumDetails, 'current.monthlyCost'),
      termDescription: _get(this.premiumDetails, 'current.policyTermMonths'),

      newRiskInformation: this.newVehicle,
      newVehicleVin: _get(this.premiumDetails, 'new.newVehicle.vin'),
      newTotalPremium: _get(this.premiumDetails, 'new.fullCost'),
      newMonthlyPremium: _get(this.premiumDetails, 'new.monthlyCost'),
      newTermDescription: _get(this.premiumDetails, 'new.policyTermMonths'),

      drivers: drivers,
      discounts: discounts,
      fees: fees,
      // AS: If we do not have the quote exp date coming back assume the current date and send the mail
      quoteExpirationDate: quoteExpDate
        ? format(addDays(new Date(quoteExpDate), 60), 'MM/DD/YYYY')
        : format(addDays(new Date(), 60), 'MM/DD/YYYY'),
      garagedAddress: _get(this.matchingvehicle, 'address.addressLine1'),
      coverages: coverages,
      orderNumber: _get(this.premiumDetails, 'orderNumber', ''),
      producerId: _get(this, 'selectedProducer.id')
    };
    return messageModelRequest;
  }
}
